function slugify(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 100);
}

type ImageUrlOptions = {
  width?: number;
  quality?: number;
};

function isEnabledFlag(value: string | undefined) {
  return /^(1|true|yes)$/i.test((value || '').trim());
}

const supabaseAssetBaseUrl = (() => {
  const viteEnv = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
  const processEnv = typeof process !== 'undefined' ? process.env : undefined;
  return (viteEnv?.VITE_SUPABASE_PUBLIC_ASSET_BASE_URL || processEnv?.VITE_SUPABASE_PUBLIC_ASSET_BASE_URL || '').replace(/\/+$/, '');
})();

const supabaseRenderImageBaseUrl = (() => {
  if (!supabaseAssetBaseUrl) return '';
  return supabaseAssetBaseUrl.replace('/storage/v1/object/public/', '/storage/v1/render/image/public/');
})();

const seoImageProxyEnabled = (() => {
  const viteEnv = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
  const processEnv = typeof process !== 'undefined' ? process.env : undefined;
  return isEnabledFlag(viteEnv?.VITE_ENABLE_SEO_IMAGE_PROXY || processEnv?.VITE_ENABLE_SEO_IMAGE_PROXY);
})();

function isSupabaseRasterUrl(src: string) {
  if (!supabaseAssetBaseUrl || !src.startsWith(`${supabaseAssetBaseUrl}/`)) return false;
  return /\.(avif|jpe?g|png|webp)(?:$|\?)/i.test(src);
}

function buildSupabaseRenderUrl(src: string, width: number, quality: number) {
  const relativePath = src.slice(supabaseAssetBaseUrl.length);
  const params = new URLSearchParams({
    width: String(width),
    quality: String(quality),
    format: 'webp',
    resize: 'contain',
  });
  return `${supabaseRenderImageBaseUrl}${relativePath}?${params.toString()}`;
}

function getFileExtension(src: string) {
  const match = src.match(/\.([a-z0-9]+)(?:$|\?)/i);
  return match?.[1]?.toLowerCase() || 'webp';
}

function normalizeLocalAssetPath(src: string) {
  if (src.startsWith('/')) {
    return src;
  }

  if (!supabaseAssetBaseUrl || !src.startsWith(`${supabaseAssetBaseUrl}/`)) {
    return '';
  }

  return `/${src.slice(supabaseAssetBaseUrl.length).replace(/^\/+/, '')}`;
}

function isLocalAsset(src: string) {
  return src.startsWith('/');
}

function isRoutableLocalAsset(src: string) {
  if (src.startsWith('/brand/timeline/')) {
    return false;
  }

  return src.startsWith('/brand/') || src.startsWith('/media/');
}

function isResizableRaster(src: string) {
  return /^(avif|jpe?g|png|webp)$/i.test(getFileExtension(src));
}

function hasGenericFileName(src: string) {
  return /(gallery-\d+|photo-\d+|general-\d+|home-[a-z0-9-]+|feature|intro|sidebar|hero|banner|bg|animation|activity|card|logo|logotype|extraordinary|food|live|[0-9a-f-]{32,}\.webp)/i.test(src);
}

function getSourceFileLabel(src: string) {
  const filename = src.split('/').pop() || '';
  return filename.replace(/\.[a-z0-9]+(?:\?.*)?$/i, '');
}

export function toSeoImageUrl(src: string | undefined, alt: string | undefined, title: string | undefined, options: ImageUrlOptions = {}) {
  if (!src || src.startsWith('/seo-images/') || src.includes('/storage/v1/render/image/')) {
    return src;
  }

  if (options.width && isSupabaseRasterUrl(src)) {
    return buildSupabaseRenderUrl(src, options.width, options.quality ?? 78);
  }

  if (!seoImageProxyEnabled) {
    return src;
  }

  const localSrc = normalizeLocalAssetPath(src);

  if (!localSrc || !isLocalAsset(localSrc) || !isRoutableLocalAsset(localSrc)) {
    return src;
  }

  if (!hasGenericFileName(localSrc) && !localSrc.startsWith('/media/')) {
    return src;
  }

  if (options.width && !isResizableRaster(localSrc)) {
    return src;
  }

  const baseLabel = options.width ? getSourceFileLabel(localSrc) : title || alt || getSourceFileLabel(localSrc) || 'gameside-caen-image';
  const slug = slugify(baseLabel) || 'gameside-caen-image';
  const extension = options.width ? 'webp' : getFileExtension(localSrc);
  const params = new URLSearchParams({ src: localSrc });

  if (options.width) {
    params.set('w', String(options.width));
  }

  if (options.quality) {
    params.set('q', String(options.quality));
  }

  return `/seo-images/${slug}.${extension}?${params.toString()}`;
}

export function toSeoImageSrcSet(src: string | undefined, alt: string | undefined, title: string | undefined, widths = [320, 480, 640, 768, 960, 1200, 1440, 1680, 1920]) {
  if (!src) {
    return undefined;
  }

  // Supabase URLs always get responsive variants via /render/image transformations
  if (isSupabaseRasterUrl(src)) {
    return widths
      .map((width) => `${buildSupabaseRenderUrl(src, width, 78)} ${width}w`)
      .join(', ');
  }

  if (!seoImageProxyEnabled) {
    return undefined;
  }

  const entries = widths
    .map((width) => {
      const url = toSeoImageUrl(src, alt, title, { width, quality: 78 });
      return url && url !== src ? `${url} ${width}w` : '';
    })
    .filter(Boolean);

  return entries.length > 0 ? entries.join(', ') : undefined;
}
